import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';

import calculateVatRate from 'bl/payment/calculateVatRate.bl';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import Tooltip from 'components/generic/Tooltip.component';
import CurrencySign from 'components/price/CurrencySign.component';
import LINK from 'constants/link.const';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonCurrency } from 'selectors/currentSalon.selectors';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import currencySign from 'utils/intl/currencySign.util';
import linkTo from 'utils/links/linkTo.util';
import createNodeKey from 'utils/react/createNodeKey.util';
import truncateString from 'utils/strings/truncateString.util';
import fixed from 'utils/values/fixed.util';

import { DownloadExcelButton } from 'reports/Common/DownloadExcelButton';

import CN from './FutureRevenueProjectionsTable.module.scss';

// NOTE: calculate total %
// taking all busy minutes (totalBusyMinutes) and / by all actual minutes ( totalActualMinutes) and * by 100

const calculateTotalOccupancyPercentage = (options) => {
  const stylistsGroupAppointments = options?.stylistsGroupAppointments || {};
  const stylistsGroupHours = options?.stylistsGroupHours || {};

  let totalBusyMinutes = 0;
  let totalActualMinutes = 0;

  for (const stylistId of Object.keys(stylistsGroupAppointments)) {
    const hours = stylistsGroupHours[stylistId];
    const busyMinutes = hours ? hours.busy_mins : 0;
    const actualMinutes = hours ? hours.actual_mins : 0;

    totalBusyMinutes += busyMinutes;
    totalActualMinutes += actualMinutes;
  }

  return 0 === totalActualMinutes ? 0 : (totalBusyMinutes / totalActualMinutes) * 100;
};

const FutureRevenueProjectionsTable = ({ appointmentsReport, salonHoursReport, startDate, endDate, salonVatRate, stylistIDInCalendarOrder, currentSign }) => {
  const { isDarkMode } = useTheme();
  const salonId = useCurrentSalonId();
  const { t } = useTranslation();

  const marketingLink = linkTo({ pattern: LINK.marketing, params: { salonId }, fault: '#' });

  const stylistGroupAppointments = appointmentsReport.lastRequestResponse.stylist_group || {};
  const stylistGroupHours = salonHoursReport.lastRequestResponse.stylist_group || {};

  const tooltipContents = useMemo(
    () => [
      {
        id: 1,
        text: t('businessReports.futureRevenue.tooltips.futureOccupancy.text'),
        label: t('businessReports.futureRevenue.tooltips.futureOccupancy.label'),
      },
      {
        id: 2,
        text: t('businessReports.futureRevenue.tooltips.futureAppointments.text'),
        label: t('businessReports.futureRevenue.tooltips.futureAppointments.label'),
      },
      {
        id: 3,
        text: t('businessReports.futureRevenue.tooltips.futureRevenue.text'),
        label: t('businessReports.futureRevenue.tooltips.futureRevenue.label'),
      },
      {
        id: 4,
        text: t('businessReports.futureRevenue.tooltips.futureAvgBookingValue.text'),
        label: t('businessReports.futureRevenue.tooltips.futureAvgBookingValue.label'),
      },
    ],
    [t],
  );

  const tableHeader = (
    <tr>
      <th className={CN.firstCell}>{t('businessReports.futureRevenue.staffHeader')}</th>
      {tooltipContents.map(({ text, label }, index) => {
        const isLast = index === tooltipContents.length - 1;
        return (
          <th key={createNodeKey({ index })}>
            <div className={CN.tableHeaderText}>
              <div className={CN.labelSpace}>{label}</div>
              <Tooltip content={text} variant={isLast ? 'quaternaryLeftToBottom' : 'quaternaryBottom'} width="max">
                <div className={CN.iconTooltip}>
                  <Icon variant="info" width="sm" />
                </div>
              </Tooltip>
            </div>
          </th>
        );
      })}
    </tr>
  );

  const tableRowsData = stylistIDInCalendarOrder
    .filter((stylist) => stylistGroupAppointments[stylist.id] && stylistGroupHours[stylist.id])
    .map((stylist) => {
      const stylistId = stylist.id;
      const appointment = stylistGroupAppointments[stylistId];
      const hours = stylistGroupHours[stylistId];
      const stylistName = appointment.name;
      const apointmentCount = appointment.count;

      const busyMinutes = hours ? hours.busy_mins : 0;
      const actualMinutes = hours ? hours.actual_mins : 0;

      // Calculate occupancy percentage with proper checks
      let occupancyPercentage = 0;
      if (actualMinutes !== 0 && !isNaN(busyMinutes) && !isNaN(actualMinutes)) {
        occupancyPercentage = ((busyMinutes / actualMinutes) * 100).toFixed(); // occupancy % = busy_mins / actual_mins * 100
      }

      const averageBookingValue = fixed(appointment.total / appointment.count);
      // calculate value after vat
      const totalAfterVat = calculateVatRate(appointment.total, salonVatRate);
      const averageBookingValueAfterVat = calculateVatRate(averageBookingValue, salonVatRate);

      return {
        stylistId,
        stylistName,
        apointmentCount,
        actualMinutes,
        occupancyPercentage,
        totalAfterVat,
        averageBookingValueAfterVat,
      };
    });

  // reorder stylist like in calendar
  const tableRows = asArray(tableRowsData).map((row) => {
    const { stylistId, stylistName, apointmentCount, actualMinutes, occupancyPercentage, totalAfterVat, averageBookingValueAfterVat } = row;

    const occupancyTooltip =
      actualMinutes === 0 || isNaN(actualMinutes) ? (
        <div className={CN.flex}>
          <Tooltip content={t('businessReports.futureRevenue.tooltips.notApplicable.text')} variant="quaternaryBottom" width="max">
            <div className={CN.flex}>
              <div className={CN.space}>{t('businessReports.futureRevenue.tooltips.notApplicable.label')} </div>
              <Icon variant="info" width="sm" />
            </div>
          </Tooltip>
        </div>
      ) : (
        `${occupancyPercentage}%`
      );

    return (
      <tr key={stylistId}>
        <td className={CN.firstCell}>{truncateString({ value: stylistName, max: 24, suffix: '...' })}</td>
        <td>{occupancyTooltip}</td>
        <td>{apointmentCount}</td>
        <td>
          <CurrencySign value={totalAfterVat} />
        </td>
        <td>
          <CurrencySign value={averageBookingValueAfterVat} />
        </td>
      </tr>
    );
  });

  const totalOccupancyPercentage = calculateTotalOccupancyPercentage({
    stylistsGroupAppointments: stylistGroupAppointments,
    stylistsGroupHours: stylistGroupHours,
  });
  // Filter the stylists to be displayed
  const visibleStylists = stylistIDInCalendarOrder.filter((stylist) => stylistGroupAppointments[stylist.id] && stylistGroupHours[stylist.id]);

  // Calculating the total count for visible stylists only
  const salonTotalCount = visibleStylists.reduce((total, stylist) => total + stylistGroupAppointments[stylist.id].count, 0);

  const salonTotalValue = Object.values(stylistGroupAppointments).reduce((total, stylist) => total + stylist.total, 0);

  const salonAverageBookingValue = salonTotalCount ? fixed(salonTotalValue / salonTotalCount) : 0;
  // calculate value after vat in footer
  const salonTotalValueAfterVat = calculateVatRate(salonTotalValue, salonVatRate);
  const salonAverageBookingValueAfterVat = calculateVatRate(salonAverageBookingValue, salonVatRate);

  const createCsv = useCallback(() => {
    const headers = [t('businessReports.futureRevenue.staffHeader'), ...tooltipContents.map((tooltip) => tooltip.label)];

    const csvData = asArray(tableRowsData).map((item) => {
      const futureOccupancy = item.actualMinutes === 0 || isNaN(item.actualMinutes) ? t('businessReports.futureRevenue.tooltips.notApplicable.label') : `${fixed(item.occupancyPercentage)}%`;

      const values = [
        item.stylistName,
        futureOccupancy,
        item.apointmentCount,
        currencySign({ value: item.totalAfterVat, currentSalonAbbr: currentSign }),
        currencySign({ value: item.averageBookingValueAfterVat, currentSalonAbbr: currentSign }),
      ];

      return Object.fromEntries(headers.map((header, i) => [header, values[i]]));
    });

    const footerValues = [
      t('businessReports.futureRevenue.salonTotal'),
      `${fixed(totalOccupancyPercentage)}%`,
      salonTotalCount,
      currencySign({ value: salonTotalValueAfterVat, currentSalonAbbr: currentSign }),
      currencySign({ value: salonAverageBookingValueAfterVat, currentSalonAbbr: currentSign }),
    ];

    const footer = headers.reduce((acc, header, index) => {
      let value = footerValues[index];
      //if headers.length !== footerValues.length, fill missing or undefined values with "N/A"
      if (value === undefined || value === null) {
        value = t('businessReports.futureRevenue.notApplicable');
      }

      acc[header] = value;
      return acc;
    }, {});

    if (footer) {
      csvData.push(footer);
    }

    return csvData;
  }, [currentSign, salonAverageBookingValueAfterVat, salonTotalCount, salonTotalValueAfterVat, tableRowsData, totalOccupancyPercentage, tooltipContents, t]);

  const hasTableData = fromAnyToBool(tableRowsData.length);

  return (
    <div
      data-bem="FutureRevenueProjectionsTable"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      <div className={CN.panelHeading}>
        <div className={CN.title}>{t('businessReports.futureRevenue.title')}</div>
        <div className={CN.buttonsContainer}>
          <NavLink to={marketingLink}>
            <Button variant="primaryForce" width="small" onClick={alwaysNoop}>
              <span className={CN.buttonText}>{t('businessReports.futureRevenue.getMoreBookings')}</span>
            </Button>
          </NavLink>
          <DownloadExcelButton getCsvData={createCsv} fileName={t('businessReports.futureRevenue.csvFileName')} disabled={!hasTableData} />
        </div>
      </div>
      <div className={CN.timeContainer}>
        <div>
          {t('businessReports.futureRevenue.fromDate')} <span className={CN.bold}>{startDate}</span>
        </div>
        <div>
          <span className={CN.gap}>{t('businessReports.futureRevenue.toDate')}</span> <span className={CN.bold}>{endDate}</span>
        </div>
      </div>
      <div className={CN.scrollContainer}>
        <table className={CN.table} id="FutureRevenueProjectionsTable">
          <thead>{tableHeader}</thead>
          <tbody>{tableRows}</tbody>
          <tfoot>
            <tr>
              <td className={CN.firstCell}>
                <span className={CN.bold}>{t('businessReports.futureRevenue.salonTotal')}</span>
              </td>

              <td>
                <span className={CN.bold}>{fixed(totalOccupancyPercentage)}%</span>
              </td>
              <td>
                <span className={CN.bold}>{salonTotalCount}</span>
              </td>
              <td>
                <span className={CN.bold}>
                  <CurrencySign value={salonTotalValueAfterVat} />
                </span>
              </td>
              <td>
                <span className={CN.bold}>
                  <CurrencySign value={salonAverageBookingValueAfterVat} />
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  appointmentsReport: state?.reports?.appointmentsReport,
  salonHoursReport: state?.reports?.salonHoursReport,
  salonVatRate: state?.reports?.salonVatRate,
  stylistIDInCalendarOrder: state?.reports?.stylistIDInCalendarOrder,
  currentSign: selectCurrentSalonCurrency(state),
});

const StaffPerformanceTableHoc = connect(mapStateToProps)(FutureRevenueProjectionsTable);

export default StaffPerformanceTableHoc;
